/**
 * `BulletGauge` (D529): a bullet chart. One horizontal axis carries up to three quiet qualitative BANDS (the `band.*`
 * roles), the measured VALUE as an ink bar and the TARGET as a tick. Whether the target is reached is said in words (the
 * caller injects `reachedLabel` / `missedLabel`), never by colour alone.
 */
import { useRef, type ReactElement } from 'react';
import { bulletLayout, buildRatioSummary, roundCoord } from '@vello/charts-core';
import { ChartToolbar, chartState, formatPercentDefault } from './bi-kit';
import type { ChartCsv, ChartExportProps } from './export';
import { cx, defaultFormatValue, joinSummary, toneColor, useElementWidth, useMotionState } from './kit';
import type { ChartBaseProps } from './line-chart';

export interface BulletGaugeProps extends ChartBaseProps, ChartExportProps {
  /** What is measured (e.g. "On-time pickups"). */
  readonly label: string;
  readonly value: number;
  readonly target?: number | null;
  /** The upper limits of the ranges, ascending (e.g. `[60, 85, 100]`). */
  readonly bands?: readonly number[];
  /** Names of the ranges, same order (e.g. "Poor", "Fair", "Good"); added to the accessible summary. */
  readonly bandLabels?: readonly string[];
  readonly min?: number;
  readonly max?: number;
  /** Lower is better (a waiting time): the target is met at or under it. Default false. */
  readonly lowerIsBetter?: boolean;
  /** Words for the status, injected. Both are needed for the status line to show. */
  readonly reachedLabel?: string;
  readonly missedLabel?: string;
  /** The word for the target in the figures and summary (e.g. "Target"). */
  readonly targetLabel?: string;
  /** Format the attainment ratio (default a whole percent). */
  readonly formatShare?: (fraction: number) => string;
}

/** The band token of the `index`-th range among `count`: the lowest range is the strongest tint; two ranges use the ends. */
function bandFill(index: number, count: number): string {
  const step = count >= 3 ? index + 1 : count === 2 ? (index === 0 ? 1 : 3) : 2;
  return `var(--chart-band-${Math.min(3, step)})`;
}

const HEADER = 20;
const BAR_TOP = 28;
const BAR_H = 22;
const STATUS_H = 18;

/** A bullet gauge: value bar over quiet bands, a target tick, and the status in words. */
export function BulletGauge(props: BulletGaugeProps): ReactElement {
  return chartState(props, HEADER + BAR_H + STATUS_H + 14, Number.isFinite(props.value)) ?? <BulletGaugePlot {...props} />;
}

function BulletGaugePlot(props: BulletGaugeProps): ReactElement {
  const format = props.formatValue ?? defaultFormatValue;
  const formatShare = props.formatShare ?? formatPercentDefault;
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, 320);
  const motion = useMotionState(props.animate);
  const bullet = bulletLayout({
    value: props.value,
    target: props.target,
    ...(props.bands !== undefined ? { bands: props.bands } : {}),
    ...(props.min !== undefined ? { min: props.min } : {}),
    ...(props.max !== undefined ? { max: props.max } : {}),
    higherIsBetter: !(props.lowerIsBetter ?? false),
  });
  const x0 = 4;
  const w = Math.max(40, width - 8);
  const at = (share: number): number => roundCoord(x0 + share * w);
  const status = bullet.reached === null ? undefined : bullet.reached ? props.reachedLabel : props.missedLabel;
  const tone = bullet.reached === null ? 'neutral' : bullet.reached ? 'success' : 'danger';
  const bandName = bullet.bandIndex !== null ? props.bandLabels?.[bullet.bandIndex] : undefined;
  const hasTarget = props.target !== undefined && props.target !== null;
  const height = HEADER + BAR_H + (status !== undefined ? STATUS_H : 0) + 14;

  const summary =
    props.ariaLabel ??
    joinSummary([
      props.title,
      buildRatioSummary({ label: props.label, value: props.value, ...(hasTarget ? { max: props.target as number } : {}), format }),
      hasTarget && props.targetLabel !== undefined ? `${props.targetLabel}: ${format(props.target as number)}` : undefined,
      bandName,
      status,
    ]);
  const csv = (): ChartCsv => ({
    header: [props.label, props.targetLabel ?? '', ...(props.bandLabels ?? [])],
    rows: [[props.value, hasTarget ? (props.target as number) : null, ...(props.bands ?? [])]],
  });

  return (
    <div className={cx('vello-chart', 'vello-chart--bullet', props.className)} data-motion={motion} data-reached={bullet.reached === null ? undefined : String(bullet.reached)}>
      <ChartToolbar props={props} title={props.title ?? props.label} csv={csv} />
      <div ref={ref} className="vello-chart__plot" role="img" aria-label={summary}>
        <svg className="vello-chart__svg" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          <text className="vello-chart__gauge-label" x={x0} y={HEADER - 6}>
            {props.label}
          </text>
          <text className="vello-chart__gauge-figures tnum" x={x0 + w} y={HEADER - 6} textAnchor="end">
            {hasTarget ? `${format(props.value)} / ${format(props.target as number)}` : format(props.value)}
          </text>
          {bullet.bands.map((b) => (
            <rect key={b.index} className="vello-chart__band-fill" x={at(b.fromShare)} y={BAR_TOP} width={Math.max(0, at(b.toShare) - at(b.fromShare))} height={BAR_H} style={{ fill: bandFill(b.index, bullet.bands.length) }} />
          ))}
          {bullet.bands.length === 0 && <rect className="vello-chart__band-fill" x={x0} y={BAR_TOP} width={w} height={BAR_H} style={{ fill: 'var(--chart-band-3)' }} />}
          <rect className="vello-chart__gauge-value vello-chart__extend" x={x0} y={BAR_TOP + 7} width={Math.max(2, roundCoord(bullet.valueShare * w))} height={BAR_H - 14} rx={2} fill={toneColor('neutral')} />
          {bullet.targetShare !== null && (
            <g className="vello-chart__target">
              <line className="vello-chart__target-halo" x1={at(bullet.targetShare)} x2={at(bullet.targetShare)} y1={BAR_TOP - 3} y2={BAR_TOP + BAR_H + 3} />
              <line className="vello-chart__target-line vello-chart__target-line--solid" x1={at(bullet.targetShare)} x2={at(bullet.targetShare)} y1={BAR_TOP - 3} y2={BAR_TOP + BAR_H + 3} />
            </g>
          )}
          {status !== undefined && (
            <text className="vello-chart__gauge-status" x={x0} y={BAR_TOP + BAR_H + 15} fill={toneColor(tone)}>
              {bullet.attainment !== null ? `${status} · ${formatShare(bullet.attainment)}` : status}
            </text>
          )}
        </svg>
      </div>
    </div>
  );
}
